import ButtonsFixed, { SpaceButtons } from '@ui/buttons/ButtonsFixed'
import ConfigDebugModal from 'features/debug/ConfigDebugModal'
import { LensCustomization } from '@ca/common'
import Page from '@shell/Page'
import Header from '@ui/elements/Header'
import { useOrderState } from '@order/hooks/useOrderState'
import { endPathname } from '@order/utils/getNextStepHref'
import { router, useNavigation } from 'expo-router'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import { useEffect, useMemo } from 'react'
import { SubmitHandler } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import useLensesHookForm from './useLensesForm'
import { useSyncActiveItem } from './useSyncActiveItem'
import useBundleType from '@order/hooks/useBundleType'
import LensesForm from './LensesForm'
import { getFlow } from '@order/utils/getFlow'
import useFlowName from '@order/hooks/useFlowName'

export default function LensesStep() {
  const { t } = useTranslation(['ui', 'configuration'])
  const text = 'configuration:lenses'
  const { bundleType } = useBundleType()
  const { orderItemId } = useSyncActiveItem()
  const resetItemCompleted = useOrderState((s) => s.resetItemCompleted)
  const config = useRemoteConfig()
  const switchActiveItem = useOrderState((s) => s.switchActiveItem)
  const setCustomization = useOrderState((s) => s.setCustomization)
  const flowName = useFlowName()

  const items = useOrderState((s) => s.order.items)
  const item = useMemo(
    () => items.find((i) => i.id === orderItemId),
    [items, orderItemId]
  )
  const form = useLensesHookForm(item?.customization)
  const hasChanged = form.formState.isDirty

  const { next } = useMemo(() => {
    const next = items.find((i) => !i.isCompleted)

    return {
      next: items.length > 1 ? next : undefined,
      isCompleted: !next,
    }
  }, [items])

  const navigation = useNavigation()
  useEffect(() => {
    const unsubscribe = navigation.addListener('beforeRemove', (e) => {
      if (e.data.action.type === 'GO_BACK' || e.data.action.type === 'RESET') {
        resetItemCompleted()
      }
    })
    return unsubscribe
  }, [navigation])

  const handleNext: SubmitHandler<LensCustomization> = (data) => {
    if (!config || !orderItemId) {
      return
    }

    setCustomization(orderItemId, data)
    if (next) {
      switchActiveItem(next.id)
      // Configure nth frame
      const flow = getFlow(config, flowName)
      const frameStepId =
        flow.stepModes.find((m) => m.mode === 'frame')?.initialStep ||
        flow.stepModes[0]?.initialStep
      router.push({
        pathname: `/configuration/steps/${frameStepId}`,
        params: {
          orderItemId: next.id,
          mode: 'frame',
        },
      })
      return
    }
    router.push({
      pathname: endPathname,
      params: {
        bundleType,
        orderItemId,
      },
    })
    return
  }

  return (
    <>
      <Page validateStaff stage="configuration">
        <Header
          title={t(`${text}.header.title`)}
          desc={t(`${text}.header.desc`)}
        />
        <LensesForm form={form} />
        <SpaceButtons />
      </Page>
      <ButtonsFixed
        buttons={[
          {
            variant: hasChanged ? 'filled' : 'outlined',
            label: hasChanged ? t('ui:save') : t('ui:skip'),
            iconEnd: 'arrow-narrow-right',
            onPress: form.handleSubmit(handleNext, console.warn),
          },
        ]}
      />
      <ConfigDebugModal />
    </>
  )
}
